import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FiUsers } from "react-icons/fi";
import { FaRegEye, FaRegEdit } from "react-icons/fa";
import { GoStop } from "react-icons/go";
import UserDetailsModal from "../components/UserDetailsModal";
import ConfirmModal from "../components/ConformModel/ConfirmModal";
import { useToast } from "@chakra-ui/react";
import { useLocation } from "react-router-dom"

import { TbLockCancel, TbLockCheck } from "react-icons/tb";
import DatePicker from "react-date-picker";
import { getUserRole } from "../components/utilities/getUserRole";
const API_URL = "/api/v1/admin/users";

const Users = () => {

    const location = useLocation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        gender: "",
        phoneNumber: "",
        dateOfBirth: "",
        location: "",
        role: "user",
        isActive: true
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [languageFilter, setLanguageFilter] = useState('all');
    const [genderFilter, setGenderFilter] = useState('all');
    const [searchTimeout, setSearchTimeout] = useState(null);
    const toast = useToast();

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [confirmAction, setConfirmAction] = useState(null);
    const [actionTargetUser, setActionTargetUser] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        genderStats: { male: 0, female: 0, other: 0 }
    });

    // State to track blocked status for each user
    const [userBlockStatus, setUserBlockStatus] = useState({});
    const currentUserRole = getUserRole().role;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const userId = params.get("userId");

        if (userId && users.length > 0) {
            const foundUser = users.find((u) => u._id === userId);

            if (foundUser) {
                setSelectedUser(foundUser);
                setViewModalOpen(true);
            }
        }
    }, [location.search, users]);

    const fetchDashboardData = useCallback(async () => {
        try {
            const [dashRes, genderRes] = await Promise.all([
                axios.get("/api/v1/admin/analytics/dashboard"),
                axios.get("/api/v1/user/getGender").catch(() => ({ data: { success: false } }))
            ]);

            if (dashRes.data.success) {
                const { totalUsers, activeUsers } = dashRes.data.data;
                const inactiveUsers = totalUsers - activeUsers;

                setDashboardData(prev => ({
                    ...prev,
                    totalUsers,
                    activeUsers,
                    inactiveUsers,
                }));
            }

            if (genderRes.data.success) {
                setDashboardData(prev => ({
                    ...prev,
                    genderStats: genderRes.data.data
                }));
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        }
    }, []);

    const fetchUsers = useCallback(async (page = 1, search = '', isActive = null, language = 'all', gender = 'all') => {
        setLoading(true);

        const params = {
            page,
            limit: 10,
            sortBy: 'createdAt',
            sortOrder: 'desc'
        };

        if (search) params.search = search;
        if (isActive !== null) params.isActive = isActive;
        if (language !== 'all') params.language = language;
        if (gender !== 'all') params.gender = gender;

        try {
            const res = await axios.get("/api/v2/admin/user", { params });
            const usersData = res.data.data || [];
            setUsers(usersData);




            if (res.data.pagination) {
                setCurrentPage(res.data.pagination.page);
                setTotalPages(res.data.pagination.totalPages);
                setTotalUsers(res.data.pagination.totalUsers);
                setHasNextPage(res.data.pagination.hasNextPage);
                setHasPrevPage(res.data.pagination.hasPrevPage);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const isActiveParam = statusFilter === 'all' ? null : statusFilter === 'active';
        fetchUsers(currentPage, searchQuery, isActiveParam, languageFilter, genderFilter);
        fetchDashboardData();
    }, [currentPage, fetchDashboardData, statusFilter, languageFilter, genderFilter, searchQuery]);

    // Update block status after block/unblock actions
    const updateUserBlockStatus = (userId, isBlocked) => {
        setUserBlockStatus(prev => ({
            ...prev,
            [userId]: isBlocked
        }));
    };

    const handleEdit = (user) => {
        setSelectedUser(user);

        let dob = "";

        if (user.dateOfBirth) {
            const d = new Date(user.dateOfBirth);


            dob = d.toISOString().split("T")[0];
        }

        setForm({
            fullName: user.fullName,
            email: user.email,
            gender: user.gender,
            phoneNumber: user.phoneNumber,
            dateOfBirth: dob,   // <-- always valid format
            location: user.location || "",
            role: user.role || "user",
            isActive: user.isActive
        });

        setModalOpen(true);
    };



    const handleView = (user) => {
        setSelectedUser(user);
        setViewModalOpen(true);
    };

    const handleSubmit = () => {
        if (selectedUser) {
            axios.put(`${API_URL}/${selectedUser._id}`, form)
                .then(() => {
                    fetchUsers(currentPage);
                    setModalOpen(false);
                    setSelectedUser(null);
                })
                .catch((error) => alert('Error updating user: ' + error.message));
        } else {
            axios.post(API_URL, form)
                .then(() => {
                    fetchUsers(currentPage);
                    setModalOpen(false);
                })
                .catch((error) => alert('Error creating user: ' + error.message));
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API_URL}/${id}`);
            fetchUsers(currentPage);
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        if (searchTimeout) clearTimeout(searchTimeout);

        const newTimeout = setTimeout(() => {
            setCurrentPage(1);
        }, 800);

        setSearchTimeout(newTimeout);
    };

    const handleStatusFilter = (filter) => {
        setStatusFilter(filter);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
        setLanguageFilter('all');
        setGenderFilter('all');
        setCurrentPage(1);
    };



    const performBlockUser = async (userId) => {
        try {
            const res = await axios.post("/api/v1/admin/users/block-user", {
                userId: userId
            });


            const isBlocked = res?.data?.data?.isBlocked ?? true;

            updateUserBlockStatus(userId, isBlocked);

            toast({
                title: "User blocked successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error("Error blocking user:", error);
            const message = error?.response?.data?.error || "Failed to block user";

            toast({
                title: message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });

        } finally {
            setConfirmModalOpen(false);
            setConfirmAction(null);
            setActionTargetUser(null);
        }
    };





    const performUnblockUser = async (userId) => {
        try {
            const res = await axios.post("/api/v1/admin/users/un-block-user", {
                userId: userId
            });

            const isBlocked = res?.data?.data?.isBlocked ?? false;

            updateUserBlockStatus(userId, isBlocked);

            toast({
                title: "User unblocked successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error("Error unblocking user:", error);
            const message = error?.response?.data?.error || "Failed to unblock user";

            toast({
                title: message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });

        } finally {
            setConfirmModalOpen(false);
            setConfirmAction(null);
            setActionTargetUser(null);
        }
    };

    const openBlockConfirm = (user) => {
        setActionTargetUser(user);
        setConfirmAction("block");
        setConfirmModalOpen(true);
    };

    const openUnblockConfirm = (user) => {
        setActionTargetUser(user);
        setConfirmAction("unblock");
        setConfirmModalOpen(true);
    };

    const calculateAge = (dob) => {
        if (!dob) return '-';
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
        return age;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getLocation = (user) => {
        if (user.location) return user.location;
        if (user.city && user.state && user.country) return `${user.city}, ${user.state}, ${user.country}`;
        if (user.city && user.country) return `${user.city}, ${user.country}`;
        if (user.city) return user.city;
        if (user.country) return user.country;
        return '-';
    };

    const mapUserToModalData = (user) => {
        if (!user) return null;

        return {
            name: user.fullName || '-',
            email: user.email || '-',
            phone: user.phoneNumber || '-',
            address: user.city || '-',
            latitude: user.coordinates.latitude || '-',
            longitude: user.coordinates.longitude || '-',
            age: calculateAge(user.dateOfBirth) || '-',
            dob: user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : '-',
            gender: user.gender || '-',
            role: user.role || '-',
            lastActive: user.lastActive ? new Date(user.lastActive).toLocaleString() : '-',
            accountCreated: user.createdAt ? new Date(user.createdAt).toLocaleString() : '-',
            followers: user.followers ? user.followers.length : 0,
            following: user.following ? user.following.length : 0,
            friends: user.friends ? user.friends.length : 0,
            networkConnections: (user.followers?.length || 0) + (user.following?.length || 0),
            lastActiveDate: user.lastActive ? new Date(user.lastActive).toLocaleDateString() : '-',
            lastActiveTime: user.lastActive ? new Date(user.lastActive).toLocaleTimeString() : '-',
            image: user.profileImage || 'https://via.placeholder.com/150'
        };
    };

    return (
        <div className="p-4 pr-1">
            {/* TOP SEARCH + FILTER BOX */}
            <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-5 mb-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2 text-2xl font-semibold text-[var(--primary-solid)]">
                        <FiUsers className="text-[var(--primary-solid)]" />
                        <span>User Management</span>
                    </div>
                    <div className="bg-blue-50 text-blue-600 px-4 py-2 rounded-lg border border-blue-100 flex items-center gap-2">
                        <span className="text-xs font-black uppercase tracking-wider">Total Users:</span>
                        <span className="text-xl font-black">{totalUsers}</span>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 flex-wrap">
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full md:w-64 px-4 py-3 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    />

                    <select
                        value={languageFilter}
                        onChange={(e) => setLanguageFilter(e.target.value)}
                        className="w-full md:w-48 px-4 py-3 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    >
                        <option value="all">All Languages</option>
                        <option value="English">English</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Telugu">Telugu</option>
                        <option value="Tamil">Tamil</option>
                        <option value="Kannada">Kannada</option>
                        <option value="Malayalam">Malayalam</option>
                        <option value="Marathi">Marathi</option>
                        <option value="Bengali">Bengali</option>
                        <option value="Gujarati">Gujarati</option>
                        <option value="Punjabi">Punjabi</option>
                        <option value="Oriya">Oriya</option>
                        <option value="Bhojpuri">Bhojpuri</option>

                    </select>

                    <select
                        value={genderFilter}
                        onChange={(e) => setGenderFilter(e.target.value)}
                        className="w-full md:w-40 px-4 py-3 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    >
                        <option value="all">All Genders</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(e) => handleStatusFilter(e.target.value)}
                        className="w-full md:w-40 px-4 py-3 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    >
                         <option value="all">All Status </option>
                        <option value="active">Active </option>
                        <option value="inactive">Inactive </option>
                        {/* <option value="all">All Status ({dashboardData.totalUsers})</option>
                        <option value="active">Active ({dashboardData.activeUsers})</option>
                        <option value="inactive">Inactive ({dashboardData.inactiveUsers})</option> */}
                    </select>
                </div>

                {(searchQuery || statusFilter !== 'all' || languageFilter !== 'all' || genderFilter !== 'all') && (
                    <div className="mt-3 text-[var(--primary-solid)] text-sm underline cursor-pointer" onClick={handleClearFilters}>
                        Clear Filters
                    </div>
                )}
            </div>

            {/* LOADING */}
            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <div className="w-10 h-10 border-4 border-gray-200 border-t-[var(--primary-solid)] rounded-full animate-spin" />
                </div>
            ) : (
                <div className="overflow-x-auto bg-white rounded-2xl shadow-lg border-2 border-gray-200">
                    <table className="w-full table-auto whitespace-nowrap text-sm text-[var(--black-text)]">
                        <thead>
                            <tr className="bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white text-xs uppercase font-semibold">
                                <th className="py-3 px-4 text-left">User</th>
                                <th className="py-3 px-4 text-left">Status</th>
                                <th className="py-3 px-4 text-left">Age</th>
                                <th className="py-3 px-4 text-left">Location</th>
                                <th className="py-3 px-4 text-left">Language</th>
                                <th className="py-3 px-4 text-left">Gender</th>
                                <th className="py-3 px-4 text-left">Joined</th>
                                <th className="py-3 px-4 text-left">Actions</th>
                            </tr>
                        </thead>

                        <tbody>

                            {users.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="8"
                                        className="py-10 text-center text-gray-500 font-medium"
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <FiUsers className="text-4xl text-gray-300" />
                                            <p>No users found</p>

                                            {(searchQuery ||
                                                statusFilter !== "all" ||
                                                languageFilter !== "all" ||
                                                genderFilter !== "all") && (
                                                    <button
                                                        onClick={handleClearFilters}
                                                        className="text-[var(--primary-solid)] underline text-sm"
                                                    >
                                                        Clear Filters
                                                    </button>
                                                )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                users.map((user, index) => {
                                    const isBlocked = userBlockStatus[user._id] || false;

                                    return (
                                        <tr key={index} className="border-t border-gray-200 hover:border-[var(--primary-solid)] hover:border-2 transition group">
                                            <td className="py-3 px-4 flex items-center gap-3">
                                                {user.profileImage ? (
                                                    <img
                                                        src={user.profileImage}
                                                        alt="Profile"
                                                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 group-hover:border-[var(--primary-solid)] transition-colors"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center group-hover:bg-gray-700">
                                                        <FiUsers className="text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-semibold text-[var(--black-text)]">{user.fullName}</div>
                                                    <div className="text-xs text-[var(--black-text)]/70">{user.phoneNumber}</div>
                                                </div>
                                            </td>

                                            <td className="py-3 px-4">
                                                <span className={`px-2 py-1 text-xs rounded-full 
                                                ${user.isActive ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}>
                                                    {user.isActive ? "Active" : "Inactive"}
                                                </span>
                                            </td>

                                            <td className="py-3 px-4 text-[var(--black-text)]">{calculateAge(user.dateOfBirth) || "-"}</td>
                                            <td className="py-3 px-4 text-[var(--black-text)]">{getLocation(user) || "-"}</td>
                                            <td className="py-3 px-4 text-center">
                                                <span className="inline-block px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-100">
                                                    {user.preferredLanguage || "N/A"}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-[var(--black-text)]">{user.gender || "-"}</td>
                                            <td className="py-3 px-4 text-[var(--black-text)]">{formatDate(user.createdAt)}</td>

                                            <td className="py-3 px-4 flex gap-3 flex-wrap">
                                                <button
                                                    className="flex items-center gap-1 text-[var(--black-text)] hover:text-[var(--yellow)]"
                                                    onClick={() => handleView(user)}
                                                >
                                                    <FaRegEye /> View
                                                </button>

                                                <button
                                                    className="flex items-center gap-1 text-[var(--black-text)] hover:text-[var(--yellow)]"
                                                    onClick={() => handleEdit(user)}
                                                >
                                                    <FaRegEdit /> Edit
                                                </button>

                                                {currentUserRole == 'super-admin' && (
                                                    <button
                                                        className="flex items-center gap-1 text-red-500 hover:text-red-800"
                                                        onClick={() => {
                                                            setActionTargetUser(user);
                                                            setConfirmAction("delete");
                                                            setConfirmModalOpen(true);
                                                        }}
                                                    >
                                                        <GoStop /> Delete
                                                    </button>
                                                )}

                                                {currentUserRole === 'super-admin' && (
                                                    user.isBlocked || isBlocked ? (
                                                        <button
                                                            onClick={() => openUnblockConfirm(user)}
                                                            className="flex items-center gap-1 text-green-600 group-hover:text-green-300 hover:text-green-300"
                                                        >
                                                            <TbLockCheck />
                                                            Unblock
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => openBlockConfirm(user)}
                                                            className="flex items-center gap-1 text-[var(--black-text)] hover:text-[var(--yellow)]"
                                                        >
                                                            <TbLockCancel /> Block
                                                        </button>
                                                    )
                                                )}






                                            </td>
                                        </tr>
                                    );
                                })
                            )}

                        </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between text-[var(--black-text)] text-sm">
                        <div>
                            Showing <b>{(currentPage - 1) * 10 + 1}</b> –
                            <b>{currentPage * 10 > totalUsers ? totalUsers : currentPage * 10}</b> of <b>{totalUsers}</b>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                className="px-3 py-1 border-2 border-gray-200 rounded hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white transition disabled:opacity-40 text-[var(--black-text)]"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={!hasPrevPage}
                            >
                                Prev
                            </button>

                            <span>Page {currentPage} / {totalPages}</span>

                            <button
                                className="px-3 py-1 border-2 border-gray-200 rounded hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white transition disabled:opacity-40 text-[var(--black-text)]"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={!hasNextPage}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {viewModalOpen && selectedUser && (
                <UserDetailsModal
                    model={mapUserToModalData(selectedUser)}
                    onClose={() => setViewModalOpen(false)}
                    userId={selectedUser._id}
                />
            )}

            {/* Edit Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 w-full max-w-lg text-[var(--black-text)]">
                        <button
                            className="absolute top-3 right-3 text-[var(--primary-solid)]"
                            onClick={() => setModalOpen(false)}
                        >
                            ✕
                        </button>

                        <h2 className="text-xl font-semibold mb-5 text-[var(--primary-solid)]">
                            {selectedUser ? "Edit User" : "Add New User"}
                        </h2>

                        <div className="space-y-4">
                            <input
                                className="w-full bg-white border-2 border-gray-200 px-4 py-2 rounded text-[var(--black-text)] focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                                placeholder="Full Name"
                                value={form.fullName}
                                onChange={e => setForm({ ...form, fullName: e.target.value })}
                            />

                            <div className="w-full">
                                <label className="block text-sm text-[var(--black-text)] mb-2">Date of Birth</label>
                                <DatePicker
                                    onChange={(date) => {
                                        if (date) {
                                            // Simple fix: use toLocaleDateString to avoid timezone issues
                                            const year = date.getFullYear();
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const formattedDate = `${year}-${month}-${day}`;

                                            setForm({ ...form, dateOfBirth: formattedDate });
                                        } else {
                                            setForm({ ...form, dateOfBirth: '' });
                                        }
                                    }}
                                    value={form.dateOfBirth ? new Date(form.dateOfBirth) : null}
                                    className="w-full bg-white rounded py-2 text-[var(--black-text)]"
                                    calendarClassName="bg-white border-2 border-gray-200 text-[var(--black-text)] rounded-lg shadow-lg max-h-80 overflow-y-auto"
                                    tileClassName="text-[var(--black-text)] hover:bg-[var(--yellow)] hover:text-white transition-colors"
                                    navigationLabel={({ date }) => (
                                        <span className="text-[var(--primary-solid)] font-semibold">
                                            {date.toLocaleString('default', { month: 'long', year: 'numeric' })}
                                        </span>
                                    )}
                                    prevLabel={
                                        <span className="text-[var(--primary-solid)] hover:text-white">‹</span>
                                    }
                                    nextLabel={
                                        <span className="text-[var(--primary-solid)] hover:text-white">›</span>
                                    }
                                    clearIcon={null}
                                    format="yyyy-MM-dd"
                                    maxDate={new Date()}
                                    required
                                />
                            </div>

                            <input
                                className="w-full bg-white border-2 border-gray-200 px-4 py-2 rounded text-[var(--black-text)] focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                                placeholder="Phone Number"
                                value={form.phoneNumber}
                                onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                            />
                            <select
                                className="w-full bg-white border-2 border-gray-200 px-4 py-2 rounded text-[var(--black-text)] focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                                value={form.gender}
                                onChange={e => setForm({ ...form, gender: e.target.value })}
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                            <input
                                className="w-full bg-white border-2 border-gray-200 px-4 py-2 rounded text-[var(--black-text)] focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                                placeholder="Location"
                                value={form.location}
                                onChange={e => setForm({ ...form, location: e.target.value })}
                            />

                            <select
                                className="w-full bg-white border-2 border-gray-200 px-4 py-2 rounded text-[var(--black-text)] focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                                value={form.isActive}
                                onChange={(e) =>
                                    setForm({ ...form, isActive: e.target.value === "true" })
                                }
                            >
                                <option value={true}>Active</option>
                                <option value={false}>Inactive</option>
                            </select>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                className="px-4 py-2 border-2 border-gray-200 rounded text-[var(--black-text)] hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white transition"
                                onClick={() => setModalOpen(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className="px-4 py-2 bg-[var(--yellow)] text-white rounded hover:bg-[var(--yellow)]/80 transition"
                                onClick={handleSubmit}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Modal for Block/Unblock/Delete */}
            {confirmModalOpen && actionTargetUser && (
                <ConfirmModal
                    isOpen={confirmModalOpen}
                    title={
                        confirmAction === "block"
                            ? "Confirm Block"
                            : confirmAction === "unblock"
                                ? "Confirm Unblock"
                                : "Confirm Deletion"
                    }
                    message={
                        confirmAction === "block"
                            ? `Are you sure you want to block ${actionTargetUser.fullName}?`
                            : confirmAction === "unblock"
                                ? `Are you sure you want to unblock ${actionTargetUser.fullName}?`
                                : `Are you sure you want to delete ${actionTargetUser.fullName}?`
                    }
                    onConfirm={() => {
                        if (confirmAction === "block") {
                            performBlockUser(actionTargetUser._id);
                        } else if (confirmAction === "unblock") {
                            performUnblockUser(actionTargetUser._id);
                        } else if (confirmAction === "delete") {
                            handleDelete(actionTargetUser._id);
                            setConfirmModalOpen(false);
                            setActionTargetUser(null);
                            setConfirmAction(null);
                        }
                    }}
                    onCancel={() => {
                        setConfirmModalOpen(false);
                        setActionTargetUser(null);
                        setConfirmAction(null);
                    }}
                    confirmButtonText={
                        confirmAction === "block" ? "Block"
                            : confirmAction === "unblock" ? "Unblock"
                                : "Delete"
                    }
                    confirmButtonColor={
                        confirmAction === "block" ? "red"
                            : confirmAction === "unblock" ? "green"
                                : "red"
                    }
                    block={confirmAction === "block" ? "block"
                        : confirmAction === "unblock" ? "unblock"
                            : "yes delete"}
                />
            )}
        </div>
    );
};

export default Users;
